import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import { Flag, Plus, RefreshCw, Search } from "lucide-react";
import { PortalHomeworkActivitiesListAssignment } from "../api/contracts";
import {
  ClinicianPortalApiError,
  clinicianPortalApi,
} from "../api/clinicianPortalApi";
import {
  ActionsColumnHeader,
  ActivityTypePill,
  ClientValuePill,
  HomeworkStatusPill,
  PortalDataTable,
  PortalTableColumn,
  SudsPill,
} from "../components/DataDisplay";
import { ActivityStepsPreview } from "../components/ActivityStepsPreview";
import { ErrorState, LoadingState } from "../components/FeedbackStates";
import {
  DeleteHomeworkDialog,
  RowActionMenu,
} from "../components/HomeworkControls";
import { PaginationControls } from "../components/PaginationControls";
import {
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import { PortalLink } from "../components/PortalLink";
import { ReportHelpTooltip } from "../components/ReportVisuals";
import { SingleSelectPillField } from "../components/SingleSelectPillField";
import {
  TableColumnsButton,
  usePortalTablePreferences,
} from "../components/TableColumnPreferences";
import { homeworkStepsEditorPath } from "../config/activityStepsNavigation";
import {
  currentHomeworkActivitiesListPath,
  withHomeworkActivitiesReturnTo,
} from "../config/homeworkActivityNavigation";
import {
  addHomeworkActivityPath,
  homeworkActivityDetailPath,
  homeworkDetailEditPath,
} from "../config/navigation";
import {
  getActivityTypePillToken,
  getHomeworkCompletionStatusPillToken,
} from "../config/palette";
import { usePortalData } from "../hooks/usePortalData";
import { usePortalMutation } from "../hooks/usePortalMutation";
import {
  CLINICIAN_PORTAL_NAVIGATION_EVENT,
  navigateWithinPortal,
  replaceWithinPortal,
  withClinicianCode,
} from "../routing/portalRoutes";
import { formatPortalDate } from "../utils/dateTime";
import {
  comparePortalDate,
  comparePortalNumber,
  comparePortalText,
  PortalSortDirection,
  stablePortalSort,
} from "../utils/tableSort";

type HomeworkActivitiesSortKey =
  | "client"
  | "homeworkName"
  | "activityType"
  | "completionStatus"
  | "dueDate"
  | "points"
  | "coins"
  | "firstSuds"
  | "secondSuds"
  | "sudsChange"
  | "valueSupportedName"
  | "assignedDate";

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

function subscribeToLocation(onStoreChange: () => void): () => void {
  window.addEventListener("popstate", onStoreChange);
  window.addEventListener(CLINICIAN_PORTAL_NAVIGATION_EVENT, onStoreChange);
  return () => {
    window.removeEventListener("popstate", onStoreChange);
    window.removeEventListener(
      CLINICIAN_PORTAL_NAVIGATION_EVENT,
      onStoreChange,
    );
  };
}

function currentLocationSearch(): string {
  return window.location.search;
}

function positiveInteger(value: string | null, fallback: number): number {
  if (!value) return fallback;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

function normalizedPageSize(value: string | null): number {
  const parsed = positiveInteger(value, DEFAULT_PAGE_SIZE);
  return PAGE_SIZE_OPTIONS.includes(
    parsed as (typeof PAGE_SIZE_OPTIONS)[number],
  )
    ? parsed
    : DEFAULT_PAGE_SIZE;
}

function normalizedSortKey(
  value: string | null,
): HomeworkActivitiesSortKey | null {
  return value === "client" ||
    value === "homeworkName" ||
    value === "activityType" ||
    value === "completionStatus" ||
    value === "dueDate" ||
    value === "points" ||
    value === "coins" ||
    value === "firstSuds" ||
    value === "secondSuds" ||
    value === "sudsChange" ||
    value === "valueSupportedName" ||
    value === "assignedDate"
    ? value
    : null;
}

function normalizedSortDirection(
  value: string | null,
): PortalSortDirection {
  return value === "desc" ? "descending" : "ascending";
}

function updateListSearch(
  update: (search: URLSearchParams) => void,
  mode: "push" | "replace",
): void {
  const search = new URLSearchParams(window.location.search);
  update(search);
  const query = search.toString();
  const href = `${window.location.pathname}${query ? `?${query}` : ""}`;
  if (mode === "replace") replaceWithinPortal(href);
  else navigateWithinPortal(href);
}

function duplicateMutationFailure(authorized: boolean) {
  return new ClinicianPortalApiError(
    "The homework assignment could not be duplicated.",
    authorized ? "UPSTREAM_ERROR" : "NOT_AUTHORIZED",
    authorized,
  );
}

function deleteMutationFailure(authorized: boolean) {
  return new ClinicianPortalApiError(
    "The homework assignment could not be deleted.",
    authorized ? "UPSTREAM_ERROR" : "NOT_AUTHORIZED",
    authorized,
  );
}

function getSudsChange(
  assignment: PortalHomeworkActivitiesListAssignment,
): number | null {
  const before = assignment.firstSuds;
  const after = assignment.secondSuds;

  return before != null && after != null ? after - before : null;
}

function SudsChange({
  assignment,
}: {
  assignment: PortalHomeworkActivitiesListAssignment;
}) {
  const sudsChange = getSudsChange(assignment);

  if (sudsChange == null) {
    return <span className="cp-empty-value">–</span>;
  }

  const trend =
    sudsChange < 0 ? "reduction" : sudsChange > 0 ? "increase" : "unchanged";
  const label =
    sudsChange < 0
      ? `SUDS decreased by ${Math.abs(sudsChange)}`
      : sudsChange > 0
        ? `SUDS increased by ${sudsChange}`
        : "No SUDS change";
  const displayValue =
    sudsChange < 0
      ? `↓ ${Math.abs(sudsChange)}`
      : sudsChange > 0
        ? `↑ ${sudsChange}`
        : "0";

  return (
    <span className="cp-suds-change" data-trend={trend} aria-label={label}>
      {displayValue}
    </span>
  );
}

export function HomeworkActivitiesPage({
  clinicianCode,
}: {
  clinicianCode: string;
}) {
  const [deleteTarget, setDeleteTarget] =
    useState<PortalHomeworkActivitiesListAssignment | null>(null);
  const [openActivityStepsId, setOpenActivityStepsId] = useState<string | null>(
    null,
  );
  const locationSearch = useSyncExternalStore(
    subscribeToLocation,
    currentLocationSearch,
    () => "",
  );
  const listSearch = useMemo(
    () => new URLSearchParams(locationSearch),
    [locationSearch],
  );
  const search = listSearch.get("q") ?? "";
  const patientIdFilter = listSearch.get("patientId") ?? "";
  const activityTypeFilter = listSearch.get("activityType") ?? "";
  const statusFilter = listSearch.get("status") ?? "";
  const sortKey = normalizedSortKey(listSearch.get("sort"));
  const sortDirection = normalizedSortDirection(listSearch.get("direction"));
  const pageParameter = listSearch.get("page");
  const pageSizeParameter = listSearch.get("pageSize");
  const requestedPage = positiveInteger(pageParameter, 1);
  const pageSize = normalizedPageSize(pageSizeParameter);
  const listReturnTo = currentHomeworkActivitiesListPath(locationSearch);
  const request = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getHomeworkActivities(clinicianCode, signal),
    [clinicianCode],
  );
  const state = usePortalData(request);
  const mutation = usePortalMutation();
  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const available = Boolean(data?.found && data.authorized && data.therapist);
  const isMutating = mutation.state.status === "pending";
  const pendingAction = isMutating ? mutation.state.action : "";
  const clientOptions = useMemo(() => {
    const clients = new Map<
      string,
      { patientId: string; nickname: string; accessCode: string }
    >();
    for (const assignment of data?.assignments ?? []) {
      if (!clients.has(assignment.patientId)) {
        clients.set(assignment.patientId, {
          patientId: assignment.patientId,
          nickname: assignment.nickname,
          accessCode: assignment.accessCode,
        });
      }
    }
    return Array.from(clients.values()).sort(
      (a, b) =>
        (a.nickname || "Unnamed client").localeCompare(
          b.nickname || "Unnamed client",
        ) || a.accessCode.localeCompare(b.accessCode),
    );
  }, [data?.assignments]);
  const activityTypeOptions = useMemo(() => {
    const activityTypes = Array.from(
      new Set(
        (data?.assignments ?? [])
          .map((assignment) => assignment.activityType.trim())
          .filter(Boolean),
      ),
    ).sort((a, b) => a.localeCompare(b));

    return [
      { id: "all-activity-types", value: "", label: "All Activity Types" },
      ...activityTypes.map((activityType) => ({
        id: activityType,
        value: activityType,
        label: activityType,
      })),
    ];
  }, [data?.assignments]);
  const statusOptions = useMemo(() => {
    const statuses = Array.from(
      new Set(
        (data?.assignments ?? [])
          .map((assignment) => assignment.completionStatus.trim())
          .filter(Boolean),
      ),
    ).sort((a, b) => a.localeCompare(b));

    return [
      { id: "all-statuses", value: "", label: "All Statuses" },
      ...statuses.map((status) => ({
        id: status,
        value: status,
        label: status,
      })),
    ];
  }, [data?.assignments]);
  const matchingAssignments = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return (data?.assignments ?? []).filter((assignment) => {
      if (patientIdFilter && assignment.patientId !== patientIdFilter) {
        return false;
      }
      if (
        activityTypeFilter &&
        assignment.activityType.trim() !== activityTypeFilter
      ) {
        return false;
      }
      if (statusFilter && assignment.completionStatus.trim() !== statusFilter) {
        return false;
      }
      if (!query) return true;
      return [
        assignment.nickname,
        assignment.accessCode,
        assignment.homeworkName,
        assignment.activityType,
        assignment.completionStatus,
        assignment.activitySteps,
        assignment.firstSuds,
        assignment.secondSuds,
        getSudsChange(assignment),
        assignment.routineTime,
        assignment.valueSupportedName,
        formatPortalDate(assignment.assignedDate),
        assignment.emailAlert ? "alert" : "",
      ]
        .join(" ")
        .toLocaleLowerCase()
        .includes(query);
    });
  }, [
    activityTypeFilter,
    data?.assignments,
    patientIdFilter,
    search,
    statusFilter,
  ]);
  const sortedAssignments = useMemo(() => {
    if (!sortKey) return matchingAssignments;
    return stablePortalSort(matchingAssignments, (left, right) => {
      switch (sortKey) {
        case "client":
          return (
            comparePortalText(left.nickname, right.nickname, sortDirection) ||
            comparePortalText(left.accessCode, right.accessCode, sortDirection)
          );
        case "dueDate":
        case "assignedDate":
          return comparePortalDate(left[sortKey], right[sortKey], sortDirection);
        case "points":
        case "coins":
        case "firstSuds":
        case "secondSuds":
          return comparePortalNumber(
            left[sortKey],
            right[sortKey],
            sortDirection,
          );
        case "sudsChange":
          return comparePortalNumber(
            getSudsChange(left),
            getSudsChange(right),
            sortDirection,
          );
        default:
          return comparePortalText(
            left[sortKey],
            right[sortKey],
            sortDirection,
          );
      }
    });
  }, [matchingAssignments, sortDirection, sortKey]);

  const updateSort = (nextSortKey: HomeworkActivitiesSortKey): void => {
    const nextDirection =
      nextSortKey === sortKey && sortDirection === "ascending"
        ? "descending"
        : "ascending";
    setOpenActivityStepsId(null);
    updateListSearch((nextSearch) => {
      nextSearch.set("sort", nextSortKey);
      nextSearch.set(
        "direction",
        nextDirection === "descending" ? "desc" : "asc",
      );
      nextSearch.delete("page");
    }, "push");
  };

  const sortProps = (key: HomeworkActivitiesSortKey) => ({
    sortDirection: sortKey === key ? sortDirection : ("none" as const),
    onSort: () => updateSort(key),
  });
  const totalAssignments = data?.assignments.length ?? 0;
  const hasActiveFilters = Boolean(
    search.trim() || patientIdFilter || activityTypeFilter || statusFilter,
  );
  const resultCount = hasActiveFilters
    ? `${matchingAssignments.length} of ${totalAssignments} ${
        totalAssignments === 1 ? "homework assignment" : "homework assignments"
      }`
    : `${totalAssignments} ${
        totalAssignments === 1 ? "homework assignment" : "homework assignments"
      }`;
  const totalPages = Math.max(
    1,
    Math.ceil(sortedAssignments.length / pageSize),
  );
  const currentPage = Math.min(requestedPage, totalPages);
  const paginatedAssignments = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedAssignments.slice(start, start + pageSize);
  }, [currentPage, pageSize, sortedAssignments]);

  useEffect(() => {
    if (
      openActivityStepsId &&
      !paginatedAssignments.some(
        (assignment) => assignment.id === openActivityStepsId,
      )
    ) {
      setOpenActivityStepsId(null);
    }
  }, [openActivityStepsId, paginatedAssignments]);

  useEffect(() => {
    if (!data) return;

    const canonicalPage = currentPage === 1 ? null : String(currentPage);
    const canonicalPageSize =
      pageSize === DEFAULT_PAGE_SIZE ? null : String(pageSize);
    if (
      pageParameter === canonicalPage &&
      pageSizeParameter === canonicalPageSize
    ) {
      return;
    }

    updateListSearch((nextSearch) => {
      if (canonicalPage) nextSearch.set("page", canonicalPage);
      else nextSearch.delete("page");
      if (canonicalPageSize) nextSearch.set("pageSize", canonicalPageSize);
      else nextSearch.delete("pageSize");
    }, "replace");
  }, [currentPage, data, pageParameter, pageSize, pageSizeParameter]);

  const duplicateHomework = async (
    assignment: PortalHomeworkActivitiesListAssignment,
  ) => {
    const result = await mutation.run(
      `duplicate:${assignment.id}`,
      async (signal) => {
        const response = await clinicianPortalApi.duplicateHomework(
          clinicianCode,
          assignment.patientId,
          assignment.id,
          signal,
        );
        if (!response.success || !response.duplicated || !response.homework) {
          throw duplicateMutationFailure(response.authorized);
        }
        return response;
      },
    );

    if (result) state.refresh();
  };

  const deleteHomework = async () => {
    if (!deleteTarget) return;
    const assignment = deleteTarget;
    const result = await mutation.run(
      `delete:${assignment.id}`,
      async (signal) => {
        const response = await clinicianPortalApi.deleteHomework(
          clinicianCode,
          assignment.patientId,
          assignment.id,
          signal,
        );
        if (
          !response.success ||
          !response.deleted ||
          response.homeworkAssignmentId !== assignment.id
        ) {
          throw deleteMutationFailure(response.authorized);
        }
        return response;
      },
    );

    if (result) {
      setDeleteTarget(null);
      state.refresh();
    }
  };

  const columns: readonly PortalTableColumn<
    PortalHomeworkActivitiesListAssignment
  >[] = [
    {
      key: "actions",
      header: <ActionsColumnHeader />,
      headerLabel: "Actions",
      initialWidth: 60,
      minWidth: 60,
      resizable: false,
      cellClassName: "cp-table-cell--compact",
      render: (assignment) => (
        <RowActionMenu
          clinicianCode={clinicianCode}
          editPath={withHomeworkActivitiesReturnTo(
            homeworkDetailEditPath(
              homeworkActivityDetailPath(
                assignment.patientId,
                assignment.id,
              ),
            ),
            listReturnTo,
          )}
          activityName={assignment.homeworkName}
          canEdit={Boolean(assignment.homeworkName)}
          canDuplicate={Boolean(assignment.homeworkName) && !isMutating}
          isDuplicating={pendingAction === `duplicate:${assignment.id}`}
          onDuplicate={() => duplicateHomework(assignment)}
          canDelete={!isMutating}
          isDeleting={pendingAction === `delete:${assignment.id}`}
          onDelete={() => {
            mutation.reset();
            setDeleteTarget(assignment);
          }}
        />
      ),
    },
    {
      key: "client",
      header: "Client",
      initialWidth: 120,
      minWidth: 120,
      ...sortProps("client"),
      render: (assignment) => (
        <span className="cp-client-summary">
          <strong>{assignment.nickname || "Unnamed client"}</strong>
          <small>{assignment.accessCode}</small>
        </span>
      ),
    },
    {
      key: "homeworkName",
      header: "Homework Activity",
      initialWidth: 240,
      minWidth: 220,
      ...sortProps("homeworkName"),
      cellClassName: "cp-table-cell--single-line",
      render: (assignment) => (
        <span
          className="cp-homework-name cp-homework-activity-name"
          title={assignment.homeworkName || undefined}
        >
          {assignment.homeworkName || "–"}
        </span>
      ),
    },
    {
      key: "activityType",
      header: "Activity Type",
      initialWidth: 220,
      minWidth: 165,
      ...sortProps("activityType"),
      cellClassName: "cp-table-cell--single-line",
      render: (assignment) => (
        <ActivityTypePill
          activityType={assignment.activityType}
          title={assignment.activityType}
        />
      ),
    },
    {
      key: "completionStatus",
      header: "Status",
      initialWidth: 155,
      minWidth: 155,
      ...sortProps("completionStatus"),
      render: (assignment) => (
        <HomeworkStatusPill status={assignment.completionStatus} />
      ),
    },
    {
      key: "dueDate",
      header: "Due Date",
      initialWidth: 105,
      minWidth: 105,
      ...sortProps("dueDate"),
      cellClassName: "cp-table-cell--date-time",
      render: (assignment) => formatPortalDate(assignment.dueDate),
    },
    {
      key: "points",
      header: "Points",
      initialWidth: 80,
      minWidth: 80,
      ...sortProps("points"),
      cellClassName: "cp-table-cell--compact",
      render: (assignment) => assignment.points,
    },
    {
      key: "coins",
      header: "Coins",
      initialWidth: 80,
      minWidth: 80,
      ...sortProps("coins"),
      cellClassName: "cp-table-cell--compact",
      render: (assignment) => assignment.coins,
    },
    {
      key: "activitySteps",
      header: "Activity Steps",
      initialWidth: 390,
      minWidth: 240,
      cellClassName: "cp-table-cell--single-line",
      render: (assignment) =>
        assignment.activitySteps ? (
          <ActivityStepsPreview
            activityName={assignment.homeworkName}
            activitySteps={assignment.activitySteps}
            isOpen={openActivityStepsId === assignment.id}
            onOpenChange={(open) =>
              setOpenActivityStepsId(open ? assignment.id : null)
            }
          />
        ) : (
          <span className="cp-empty-value">–</span>
        ),
    },
    {
      key: "editSteps",
      header: "Edit Steps",
      initialWidth: 125,
      minWidth: 110,
      resizable: false,
      render: (assignment) => (
        <a
          className="cp-edit-steps-button"
          href={homeworkStepsEditorPath({
            clinicianCode,
            patientId: assignment.patientId,
            homeworkAssignmentId: assignment.id,
            returnUrl: window.location.href,
          })}
        >
          Edit Steps
        </a>
      ),
    },
    {
      key: "firstSuds",
      header: "Before",
      initialWidth: 72,
      minWidth: 72,
      ...sortProps("firstSuds"),
      resizable: false,
      cellClassName: "cp-table-cell--compact",
      render: (assignment) => (
        <SudsPill
          value={assignment.firstSuds}
          emptyVariant="circle"
          emptyLabel="No before rating"
        />
      ),
    },
    {
      key: "secondSuds",
      header: "After",
      initialWidth: 72,
      minWidth: 72,
      ...sortProps("secondSuds"),
      resizable: false,
      cellClassName: "cp-table-cell--compact",
      render: (assignment) => (
        <SudsPill
          value={assignment.secondSuds}
          emptyVariant="circle"
          emptyLabel="No after rating"
        />
      ),
    },
    {
      key: "sudsChange",
      header: "Change",
      initialWidth: 90,
      minWidth: 90,
      ...sortProps("sudsChange"),
      resizable: false,
      cellClassName: "cp-table-cell--compact",
      render: (assignment) => <SudsChange assignment={assignment} />,
    },
    {
      key: "emailAlert",
      header: "Alert",
      initialWidth: 68,
      minWidth: 68,
      resizable: false,
      cellClassName: "cp-table-cell--compact",
      render: (assignment) => {
        const alertLabel = assignment.emailAlert
          ? "Elevated SUDS alert sent"
          : "No elevated SUDS alert";
        const alertDefinition = assignment.emailAlert
          ? "Elevated SUDS Alert · The OCD Homework Assistant detected elevated SUDS during this activity and emailed the client’s therapist."
          : "No Elevated SUDS Alert · No elevated SUDS alert was sent for this activity.";

        return (
          <span className="cp-history-alert-tooltip">
            <ReportHelpTooltip
              label={alertLabel}
              ariaLabel={alertLabel}
              definition={alertDefinition}
              trigger={
                <span
                  className="cp-history-alert-indicator"
                  data-active={assignment.emailAlert}
                  aria-hidden="true"
                >
                  <Flag aria-hidden="true" />
                </span>
              }
            />
          </span>
        );
      },
    },
    {
      key: "routineTime",
      header: <span title="Routine Time">Duration</span>,
      headerLabel: "Routine Time",
      initialWidth: 90,
      minWidth: 90,
      resizable: false,
      cellClassName: "cp-table-cell--compact",
      render: (assignment) =>
        assignment.routineTime.trim() || (
          <span className="cp-empty-value">–</span>
        ),
    },
    {
      key: "valueSupportedName",
      header: "Value",
      initialWidth: 160,
      minWidth: 160,
      ...sortProps("valueSupportedName"),
      render: (assignment) => (
        <ClientValuePill value={assignment.valueSupportedName} />
      ),
    },
    {
      key: "assignedDate",
      header: "Assigned",
      initialWidth: 112,
      minWidth: 112,
      ...sortProps("assignedDate"),
      cellClassName: "cp-table-cell--date-time",
      render: (assignment) => formatPortalDate(assignment.assignedDate),
    },
    {
      key: "conversation",
      header: "Conversation",
      initialWidth: 130,
      minWidth: 115,
      resizable: false,
      sticky: "right",
      render: (assignment) =>
        assignment.conversationReportUrl ? (
          <a
            className="cp-conversation-link"
            href={assignment.conversationReportUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            View
          </a>
        ) : (
          <span className="cp-empty-value">–</span>
        ),
    },
  ];
  const tablePreference = usePortalTablePreferences({
    clinicianCode,
    tableKey: "homework_activities",
    columns,
    rules: [
      {
        key: "actions",
        hideable: false,
        movable: false,
        position: "left",
      },
      { key: "client", hideable: false },
      { key: "homeworkName", hideable: false },
      {
        key: "conversation",
        movable: false,
        position: "right",
      },
    ],
  });

  const refresh = () => {
    mutation.reset();
    state.refresh();
  };

  return (
    <ClinicianPortalLayout
      activeGlobalKey="homework"
      clinicianCode={clinicianCode}
      therapistRole={data?.therapist?.role}
    >
      <PortalHeader
        title="Homework Activities"
        clinicianName={available ? data?.therapist?.therapistName : undefined}
      />
      <div className="cp-page-content cp-page-content--wide cp-page-content--sticky-list cp-page-content--compact-footer cp-homework-activities">
        {state.status === "loading" ? (
          <LoadingState label="Loading homework activities" />
        ) : null}
        {state.status === "error" ? (
          <ErrorState error={state.error} onRetry={state.refresh} />
        ) : null}
        {data && !available ? (
          <ErrorState
            error={new Error("Homework activities are not available")}
          />
        ) : null}
        {available ? (
          <section aria-label="Homework Activities list">
            <div className="cp-content-header">
              <div>
                <p>View homework assigned across your clients.</p>
              </div>
              <strong className="cp-record-count">{resultCount}</strong>
            </div>
            <div className="cp-list-toolbar cp-list-toolbar--homework">
              <div className="cp-list-toolbar__filters cp-list-toolbar__filters--homework">
                <label className="cp-search-control">
                  <Search aria-hidden="true" />
                  <span className="cp-visually-hidden">Search homework</span>
                  <input
                    type="search"
                    value={search}
                    placeholder="Search homework"
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setOpenActivityStepsId(null);
                      updateListSearch((nextSearch) => {
                        if (nextValue) nextSearch.set("q", nextValue);
                        else nextSearch.delete("q");
                        nextSearch.delete("page");
                      }, "replace");
                    }}
                  />
                </label>
                <label className="cp-homework-list-filter">
                  <span className="cp-visually-hidden">Filter by Client</span>
                  <select
                    className="cp-form-input"
                    aria-label="Filter by Client"
                    value={patientIdFilter}
                    onChange={(event) => {
                      const nextValue = event.currentTarget.value;
                      setOpenActivityStepsId(null);
                      updateListSearch((nextSearch) => {
                        if (nextValue) nextSearch.set("patientId", nextValue);
                        else nextSearch.delete("patientId");
                        nextSearch.delete("page");
                      }, "push");
                    }}
                  >
                    <option value="">All Clients</option>
                    {clientOptions.map((client) => (
                      <option key={client.patientId} value={client.patientId}>
                        {client.nickname || "Unnamed client"} (
                        {client.accessCode})
                      </option>
                    ))}
                  </select>
                </label>
                <label className="cp-homework-list-filter cp-homework-list-filter--activity-type">
                  <span className="cp-visually-hidden">
                    Filter by Activity Type
                  </span>
                  <SingleSelectPillField
                    id="homework-activity-type-filter"
                    value={activityTypeFilter}
                    options={activityTypeOptions}
                    ariaLabel="Filter by Activity Type"
                    placeholder="All Activity Types"
                    searchPlaceholder="Search Activity Types"
                    emptyMessage="No Activity Types found"
                    resolveColor={(option) =>
                      option.value
                        ? getActivityTypePillToken(option.label)
                        : "neutral"
                    }
                    onChange={(nextValue) => {
                      setOpenActivityStepsId(null);
                      updateListSearch((nextSearch) => {
                        if (nextValue) {
                          nextSearch.set("activityType", nextValue);
                        } else {
                          nextSearch.delete("activityType");
                        }
                        nextSearch.delete("page");
                      }, "push");
                    }}
                  />
                </label>
                <label className="cp-homework-list-filter cp-homework-list-filter--status">
                  <span className="cp-visually-hidden">Filter by Status</span>
                  <SingleSelectPillField
                    id="homework-status-filter"
                    value={statusFilter}
                    options={statusOptions}
                    ariaLabel="Filter by Status"
                    placeholder="All Statuses"
                    searchPlaceholder="Search Statuses"
                    emptyMessage="No Statuses found"
                    resolveColor={(option) =>
                      option.value
                        ? getHomeworkCompletionStatusPillToken(option.value)
                        : "neutral"
                    }
                    onChange={(nextValue) => {
                      setOpenActivityStepsId(null);
                      updateListSearch((nextSearch) => {
                        if (nextValue) nextSearch.set("status", nextValue);
                        else nextSearch.delete("status");
                        nextSearch.delete("page");
                      }, "push");
                    }}
                  />
                </label>
              </div>
              <div className="cp-content-header__actions cp-list-toolbar__actions">
                <TableColumnsButton preference={tablePreference} />
                <button
                  type="button"
                  className="cp-button cp-button--secondary cp-button--icon-only"
                  aria-label={
                    state.status === "refreshing" ? "Refreshing…" : "Refresh"
                  }
                  title={
                    state.status === "refreshing" ? "Refreshing…" : "Refresh"
                  }
                  data-loading={state.status === "refreshing" || undefined}
                  disabled={state.status === "refreshing" || isMutating}
                  onClick={refresh}
                >
                  <RefreshCw aria-hidden="true" />
                </button>
                <PortalLink
                  className="cp-button cp-button--primary"
                  path={addHomeworkActivityPath}
                  clinicianCode={clinicianCode}
                >
                  <Plus aria-hidden="true" />
                  Add Activity
                </PortalLink>
              </div>
            </div>

            {mutation.state.status === "error" &&
            !mutation.state.action.startsWith("delete:") ? (
              <div
                className="cp-inline-notice cp-inline-notice--error"
                role="alert"
              >
                <div>
                  <strong>Unable to duplicate that homework assignment.</strong>
                  <span>Please try again.</span>
                </div>
                <button type="button" onClick={mutation.reset}>
                  Dismiss
                </button>
              </div>
            ) : null}
            {mutation.state.status === "success" ? (
              <div className="cp-inline-notice" role="status">
                {mutation.state.action.startsWith("delete:")
                  ? "Homework activity deleted."
                  : "Homework assignment duplicated."}
              </div>
            ) : null}

            {matchingAssignments.length ? (
              <>
                <PortalDataTable
                  caption="Homework Activities"
                  density="compact"
                  columns={tablePreference.visibleColumns}
                  preferredColumnWidths={tablePreference.columnWidths}
                  onColumnWidthsChange={tablePreference.updateColumnWidths}
                  records={paginatedAssignments}
                  getRecordKey={(assignment) => assignment.id}
                  getRecordOpenLabel={(assignment) =>
                    `Open ${assignment.homeworkName || "homework assignment"}`
                  }
                  isRecordOpenable={(assignment) =>
                    Boolean(assignment.homeworkName)
                  }
                  onRecordOpen={(assignment) =>
                    navigateWithinPortal(
                      withClinicianCode(
                        withHomeworkActivitiesReturnTo(
                          homeworkActivityDetailPath(
                            assignment.patientId,
                            assignment.id,
                          ),
                          listReturnTo,
                        ),
                        clinicianCode,
                      ),
                    )
                  }
                />
                <PaginationControls
                  currentPage={currentPage}
                  pageSize={pageSize}
                  totalItems={matchingAssignments.length}
                  itemLabel="homework assignments"
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  onPageChange={(page) => {
                    setOpenActivityStepsId(null);
                    updateListSearch((nextSearch) => {
                      if (page > 1) nextSearch.set("page", String(page));
                      else nextSearch.delete("page");
                    }, "push");
                  }}
                  onPageSizeChange={(nextPageSize) => {
                    setOpenActivityStepsId(null);
                    updateListSearch((nextSearch) => {
                      if (nextPageSize === DEFAULT_PAGE_SIZE) {
                        nextSearch.delete("pageSize");
                      } else {
                        nextSearch.set("pageSize", String(nextPageSize));
                      }
                      nextSearch.delete("page");
                    }, "replace");
                  }}
                />
              </>
            ) : (
              <div className="cp-homework-table__empty">
                <h3>No matching homework</h3>
                <p>
                  {data?.assignments.length
                    ? "Try a different search or filter."
                    : "No homework assignments are available."}
                </p>
              </div>
            )}
            <DeleteHomeworkDialog
              homeworkName={deleteTarget?.homeworkName}
              isOpen={Boolean(deleteTarget)}
              isDeleting={Boolean(
                deleteTarget &&
                  mutation.state.status === "pending" &&
                  mutation.state.action === `delete:${deleteTarget.id}`,
              )}
              hasError={Boolean(
                deleteTarget &&
                  mutation.state.status === "error" &&
                  mutation.state.action === `delete:${deleteTarget.id}`,
              )}
              onCancel={() => {
                setDeleteTarget(null);
                mutation.reset();
              }}
              onConfirm={deleteHomework}
            />
          </section>
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
